import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";

export const HealthScoreMockup = () => {
  const score = 78;
  const circumference = 2 * Math.PI * 22;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-400 p-3 md:p-4 flex flex-col items-center justify-between shadow-xl overflow-hidden relative">
      {/* Ambient effects */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-white/5" />
      <div className="absolute -top-6 -right-6 w-20 h-20 bg-teal-300/30 rounded-full blur-2xl" />
      <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-emerald-300/20 rounded-full blur-xl" />
      
      {/* Circular score gauge */}
      <div className="relative z-10 flex-1 flex items-center justify-center">
        <div className="relative">
          <svg className="w-14 h-14 md:w-16 md:h-16 -rotate-90">
            {/* Background track */}
            <circle
              cx="50%"
              cy="50%"
              r="22"
              fill="none"
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="5"
            />
            {/* Colored progress with gradient effect */}
            <motion.circle
              cx="50%"
              cy="50%"
              r="22"
              fill="none"
              stroke="url(#scoreGradient)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              whileInView={{ strokeDashoffset }}
              transition={{ delay: 0.3, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            />
            <defs>
              <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="rgba(255,255,255,0.7)" />
              </linearGradient>
            </defs>
          </svg>
          
          {/* Score text */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, type: "spring" }}
            className="absolute inset-0 flex flex-col items-center justify-center"
          >
            <span className="text-white font-bold text-lg md:text-xl leading-none">{score}</span>
            <span className="text-white/60 text-[8px] font-medium">/ 100</span>
          </motion.div>
        </div>
      </div>

      {/* Document status badges */}
      <div className="relative z-10 w-full space-y-1">
        {/* Valid document */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
          className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-lg px-2 py-1"
        >
          <CheckCircle2 className="w-3 h-3 text-white" />
          <span className="text-[8px] md:text-[9px] text-white font-medium flex-1">CNI</span>
          <span className="text-[7px] text-white/70">OK</span>
        </motion.div>
        
        {/* Expiring document */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.15 }}
          className="flex items-center gap-1.5 bg-amber-500/90 backdrop-blur-sm rounded-lg px-2 py-1 shadow-lg"
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <AlertTriangle className="w-3 h-3 text-white" />
          </motion.div>
          <span className="text-[8px] md:text-[9px] text-white font-medium flex-1">Passeport</span>
          <div className="flex items-center gap-0.5">
            <Clock className="w-2 h-2 text-white/80" />
            <span className="text-[7px] text-white/90">15j</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
